import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { locationApi } from '../../api/locationApi';
import {
  LocationDto,
  LocationCategoryDto,
  LocationWhitelistDto,
  CreateLocation,
  UpdateLocation,
  CreateLocationCategory,
  UpdateLocationCategory,
  AddLocationWhitelist,
  UpdateLocationWhitelist,
  LocationListResponse,
} from '../../types';

interface LocationState {
  locations: LocationDto[];
  locationTotalCount: number;
  locationPageNumber: number;
  locationPageSize: number;
  categories: LocationCategoryDto[];
  whitelist: LocationWhitelistDto[];
  selectedLocation: LocationDto | null;
  loading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  locations: [],
  locationTotalCount: 0,
  locationPageNumber: 1,
  locationPageSize: 10,
  categories: [],
  whitelist: [],
  selectedLocation: null,
  loading: false,
  error: null,
};

// ─── Location Thunks ─────────────────────────────────────

export const fetchLocations = createAsyncThunk(
  'locations/fetchAll',
  async (
    {
      searchTerm,
      categoryId,
      isActive,
      pageNumber,
      pageSize,
    }: {
      searchTerm?: string;
      categoryId?: string;
      isActive?: boolean;
      pageNumber?: number;
      pageSize?: number;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      return await locationApi.getAll(searchTerm, categoryId, isActive, pageNumber, pageSize);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch locations');
    }
  }
);

export const fetchLocationById = createAsyncThunk(
  'locations/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await locationApi.getById(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch location');
    }
  }
);

export const createLocation = createAsyncThunk(
  'locations/create',
  async (data: CreateLocation, { rejectWithValue }) => {
    try {
      return await locationApi.create(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create location');
    }
  }
);

export const updateLocation = createAsyncThunk(
  'locations/update',
  async ({ id, data }: { id: string; data: UpdateLocation }, { rejectWithValue }) => {
    try {
      await locationApi.update(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update location');
    }
  }
);

export const deleteLocation = createAsyncThunk(
  'locations/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await locationApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete location');
    }
  }
);

// ─── Category Thunks ─────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  'locations/fetchCategories',
  async ({ searchTerm, isActive }: { searchTerm?: string; isActive?: boolean } = {}, { rejectWithValue }) => {
    try {
      return await locationApi.getCategories(searchTerm, isActive);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'locations/createCategory',
  async (data: CreateLocationCategory, { rejectWithValue }) => {
    try {
      return await locationApi.createCategory(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create category');
    }
  }
);

export const updateCategory = createAsyncThunk(
  'locations/updateCategory',
  async ({ id, data }: { id: string; data: UpdateLocationCategory }, { rejectWithValue }) => {
    try {
      await locationApi.updateCategory(id, data);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update category');
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'locations/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await locationApi.deleteCategory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete category');
    }
  }
);

// ─── Whitelist Thunks ────────────────────────────────────

export const fetchWhitelist = createAsyncThunk(
  'locations/fetchWhitelist',
  async ({ locationId, employeeId }: { locationId?: string; employeeId?: string } = {}, { rejectWithValue }) => {
    try {
      return await locationApi.getWhitelist(locationId, employeeId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch whitelist');
    }
  }
);

export const addWhitelist = createAsyncThunk(
  'locations/addWhitelist',
  async (data: AddLocationWhitelist, { rejectWithValue }) => {
    try {
      return await locationApi.addWhitelist(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add whitelist entry');
    }
  }
);

export const removeWhitelist = createAsyncThunk(
  'locations/removeWhitelist',
  async (id: string, { rejectWithValue }) => {
    try {
      await locationApi.removeWhitelist(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove whitelist entry');
    }
  }
);

export const updateWhitelist = createAsyncThunk(
  'locations/updateWhitelist',
  async ({ id, data }: { id: string; data: UpdateLocationWhitelist }, { rejectWithValue }) => {
    try {
      await locationApi.updateWhitelist(id, data);
      return { id, data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update whitelist entry');
    }
  }
);

const locationSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    clearSelectedLocation: (state) => {
      state.selectedLocation = null;
    },
    clearLocationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch locations
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action: PayloadAction<LocationListResponse>) => {
        state.loading = false;
        state.locations = action.payload.items;
        state.locationTotalCount = action.payload.totalCount;
        state.locationPageNumber = action.payload.pageNumber;
        state.locationPageSize = action.payload.pageSize;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by id
      .addCase(fetchLocationById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLocationById.fulfilled, (state, action: PayloadAction<LocationDto>) => {
        state.loading = false;
        state.selectedLocation = action.payload;
      })
      .addCase(fetchLocationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create location
      .addCase(createLocation.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLocation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete location
      .addCase(deleteLocation.fulfilled, (state, action: PayloadAction<string>) => {
        state.locations = state.locations.filter((l) => l.id !== action.payload);
      })
      // Categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<LocationCategoryDto[]>) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteCategory.fulfilled, (state, action: PayloadAction<string>) => {
        state.categories = state.categories.filter((c) => c.id !== action.payload);
      })
      // Whitelist
      .addCase(fetchWhitelist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWhitelist.fulfilled, (state, action: PayloadAction<LocationWhitelistDto[]>) => {
        state.loading = false;
        state.whitelist = action.payload;
      })
      .addCase(fetchWhitelist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateWhitelist.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeWhitelist.fulfilled, (state, action: PayloadAction<string>) => {
        state.whitelist = state.whitelist.filter((w) => w.id !== action.payload);
      });
  },
});

export const { clearSelectedLocation, clearLocationError } = locationSlice.actions;
export default locationSlice.reducer;
